import S from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { IAjax } from 'lib/Ajax';
import { AutoSavingDraftObject } from 'lib/autoSave';
import UOM from 'lib/UOM';

import { Entrypoints } from 'core/app/entrypoints';

import { Berth } from 'data/models/Berth';
import { Domain, DomainOptions } from 'data/models/Domain';
import { PierGroup } from 'data/models/PierGroup';
import { Port } from 'data/models/Port';
import { SetPierGroupFootmarksRequest, SetBerthFootmarksRequest } from 'data/models/SetPierGroupFootmarksRequest';

import { AddBerth, AddBerthParams } from 'common/models/AddBerth';

import { PierGroupFootmarksParams, PierGroupFootmarks } from 'portcall/piergroupfootmarks/model';

export enum PageMode {
    View,
    Arrange,
    Link
}

export interface ManageBerthsParams {
    portId : number | null;
    mode : PageMode;
}

export function ManageBerths(
    loader : CachedModelLoader<Domain, DomainOptions>,
    domain : Domain,
    ajax : IAjax,
    entrypoints : Entrypoints,
    addBerthModel : (params : AddBerthParams) => AddBerth,
    pgFootmarksModel : (params : PierGroupFootmarksParams) => PierGroupFootmarks,
    { portId, mode } : ManageBerthsParams
) {
    const selectedPortId = S.data(portId),
        pageMode = S.data(mode),
        ports = domain.Ports
            .sort((p1, p2) => p1.Order() - p2.Order())
            .map(p => PortViewModel(p, ajax, entrypoints, selectedPortId, pgFootmarksModel)),
        editLinkedBerths = EditLinkedBerthsViewModel(ports),
        addBerth = addBerthModel({ SchedulingAuthorityId: null });

    S.on(pageMode, () => {
        if (pageMode() === PageMode.Link) ports().forEach(p => p.SelectedBerths.ClearSelectedBerths());
    });

    loader({ Id: S.sample(domain.Id) }, 30);

    return {
        SelectedPortId: selectedPortId,
        PageMode: pageMode,
        Ports: ports,
        EditLinkedBerths: editLinkedBerths,
        AddBerth: addBerth
    };
}
export type ManageBerths = ReturnType<typeof ManageBerths>;

function EditLinkedBerthsViewModel(ports : ArraySignal<PortViewModel>) {
    const portsWithSelectedBerths = ports.filter(p => p.Show() && p.SelectedBerths.HasSelectedBerths()),
        invalidLinkedBerths = () => portsWithSelectedBerths().length > 1,
        portWithSelectedBerths = () => portsWithSelectedBerths().length === 0 ? null : portsWithSelectedBerths()[0];

    return {
        InvalidBerthSelection: invalidLinkedBerths,
        PortWithSelectedBerths: portWithSelectedBerths
    };
}

function PortViewModel(
    port : Port,
    ajax : IAjax,
    entrypoints : Entrypoints,
    selectedPortId : () => number | null,
    pgFootmarksModel : (params : PierGroupFootmarksParams) => PierGroupFootmarks
) {
    const show = () => selectedPortId() === null || selectedPortId() === port.Id(),
        pierGroups = port.PierGroups
            .filter(pg => pg.Berths().length > 0)
            .map(pg => PierGroupViewModel(pg, ajax, entrypoints))
            .sort((pg1, pg2) => pg1.Model.Order() - pg2.Model.Order()),
        selectedBerths = PortSelectedBerthsViewModel(port, pierGroups, ajax, entrypoints, pgFootmarksModel);

    return {
        Model: port,
        Show: show,
        PierGroups: pierGroups,
        SelectedBerths: selectedBerths,
        OnSortPierGroups
    };

    function OnSortPierGroups(pierGroup : PierGroupViewModel, _pierGroups : PierGroupViewModel[]) {
        S.freeze(() => {
            _pierGroups.forEach((pg, idx) => pg.Model.Order(idx));
        });
    }

}
export type PortViewModel = ReturnType<typeof PortViewModel>;

function PortSelectedBerthsViewModel(
    port : Port,
    pierGroups : ArraySignal<PierGroupViewModel>,
    ajax : IAjax,
    entrypoints : Entrypoints,
    pgFootmarksModel : (params : PierGroupFootmarksParams) => PierGroupFootmarks
) {
    const
        linkClient = ajax.Url<SetPierGroupFootmarksRequest, unknown>(() => entrypoints.LinkBerths({ Id: port.Id() }), {
            include: [r => r.Berths],
            useCache: true
        }),
        selectedBerths = lift(pierGroups.reduce((list, next) => list.concat(next.SelectedBerths()), () => [] as BerthViewModel[])),
        req = new SetPierGroupFootmarksRequest(),
        berthModelsToBerthReqs = new Map<BerthViewModel, SetBerthFootmarksRequest>(),
        NewPierGroupFootmarks = pgFootmarksModel({
            req,
            pierLengthUOM: port.PierLengthUOM,
            // TODO: Maybe add a UOM toggle? For now, just setting display UOM to the port's Length UOM.
            displayUOM: () => port.PierLengthUOM() ?? UOM.Foot
        }),
        Linking = S.value(false);

    selectedBerths.forEach(
        b => S.sample(() => {
            // If a new berth is selected, add a matching request to the end of req.Berths. Add
            // to the end to avoid clobbering any reordering that's already been applied to
            // req.Berths.
            const
                prevReq = req.Berths().length ? req.Berths()[req.Berths().length - 1] : null,
                // Set new request to start where the previous one ended, or else honor the
                // berth's current StartMark if this is the first berth selected. Users will
                // get a chance to override from here.
                newStartMark = prevReq?.StartMark.add(prevReq.Length) ?? b.Model.StartMark,
                berthReq = new SetBerthFootmarksRequest({
                    Id: b.Model.Id(),
                    StartMark: {
                        Num: newStartMark.Num(),
                        UOM: newStartMark.UOM()
                    },
                    Length: {
                        Num: b.Model.Length.Num(),
                        UOM: b.Model.Length.UOM()
                    }
                });
            berthModelsToBerthReqs.set(b, berthReq);
            req.Berths.push(berthReq);
        }),
        b => {
            // If a berth is deselected, remove matching request from req.Berths.
            req.Berths.remove(berthModelsToBerthReqs.get(b)!)
            berthModelsToBerthReqs.delete(b);
        }
    );

    return {
        HasSelectedBerths: () => selectedBerths().length > 0,
        NewPierGroupFootmarks,
        CanLink: () => selectedBerths().length > 1 && NewPierGroupFootmarks.FootmarksAreValid(),
        Link,
        Linking,
        ClearSelectedBerths
    };

    function Link() {
        if (Linking()) return;
        Linking(true);
        linkClient.Post(req).finally(() => Linking(false));
        // TH 12/18/2016 -- Used to need code here to uncheck berths selected
        // for linking. No longer needed as of 2/16/2016 b/c LinkBerthsController
        // now constructs a new PierGroup, which means we'll get a new
        // PierGroupViewModel client-side w/ new BerthViewModels.
    }

    function ClearSelectedBerths() {
        S.freeze(() => {
            selectedBerths().forEach(b => b.SelectedForLinking(false));
        });
    }
}
export type PortSelectedBerthsViewModel = ReturnType<typeof PortSelectedBerthsViewModel>;

function PierGroupViewModel(
    pierGroup : PierGroup,
    ajax : IAjax,
    entrypoints : Entrypoints
) {
    const unlinkUrl = () => entrypoints.UnlinkBerths({ Id: pierGroup.Id() }),
        unlinkClient = ajax.Url<{}, DomainOptions>(unlinkUrl, { useCache: true }),
        autosaveClient = ajax.Url<PierGroup>(() => pierGroup.Url() || ''),
        draft = AutoSavingDraftObject(pierGroup, autosaveClient),
        berths = pierGroup.Berths
            .sort(Berth.StartMarkOrder)
            .map(BerthViewModel),
        selectedBerths = berths.filter(b => b.SelectedForLinking());

    return {
        Model: draft,
        Berths: berths,
        SelectedBerths: selectedBerths,
        Unlink
    };

    function Unlink() {
        unlinkClient.Post({});
    }
}
export type PierGroupViewModel = ReturnType<typeof PierGroupViewModel>;

function BerthViewModel(berth : Berth) {
    const selectedForLinking = S.data(false);

    return {
        Model: berth,
        SelectedForLinking: selectedForLinking,
        NavigateToProfile: () => window.location.hash = '#!berthprofile?id=' + berth.Id()
    };
}
export type BerthViewModel = ReturnType<typeof BerthViewModel>;


// WEBPACK FOOTER //
// ./apps/portcall/manageberths/model.ts